/**
 * La cle d'un overlay d'OBS.
 *
 * Elle vit dans le **fragment** de l'adresse (`/overlay/essai#cle=...`), que le navigateur n'envoie jamais
 * au serveur : elle ne passe ni dans les journaux de nginx ni dans ceux de Caddy. Elle part ensuite dans un
 * en-tete (`Authorization: Cle <cle>`), jamais dans une adresse ni dans un corps.
 *
 * `lireCle` la lit, la garde en memoire (la page n'a plus son fragment apres), la recopie dans le
 * `sessionStorage` de la page (OBS recharge la source avec l'adresse d'origine, mais le fragment a ete efface :
 * sans cette copie l'overlay perdrait sa cle au premier rechargement), et efface le fragment de l'historique :
 * ni une capture de la barre d'adresse ni le panneau de l'historique ne la montrent plus. Toute lecture ou
 * ecriture du stockage peut lever (stockage bloque) : la page marche sans.
 */

/** Le nom sous lequel la cle est gardee dans le `sessionStorage`. */
export const CLE_STOCKEE = "castellan.overlay.cle";

/** La forme d'une cle generee (base64url) ; large, pour ne pas refuser une cle d'une autre longueur. */
const FORME = /^[A-Za-z0-9_-]{1,128}$/;

let enMemoire: string | null = null;

function lireStockee(): string | null {
  try {
    const valeur = window.sessionStorage.getItem(CLE_STOCKEE);
    return valeur !== null && FORME.test(valeur) ? valeur : null;
  } catch {
    return null;
  }
}

function ecrireStockee(cle: string): void {
  try {
    window.sessionStorage.setItem(CLE_STOCKEE, cle);
  } catch {
    // Stockage bloque ou plein : la cle reste en memoire, c'est tout.
  }
}

/**
 * Lit `#cle=...` dans `location`, la garde et retire le fragment de l'adresse. Sans fragment, rend la cle deja
 * lue, ou celle du `sessionStorage` (ou `null`). Une valeur qui n'a pas la forme d'une cle est ecartee, et effacee quand meme.
 */
export function lireCle(location: Location = window.location): string | null {
  const brute = new URLSearchParams(location.hash.replace(/^#/, "")).get("cle");
  if (brute !== null && brute !== "") {
    if (FORME.test(brute)) {
      enMemoire = brute;
      ecrireStockee(brute);
    }
    // Le chemin et la recherche restent : seul le fragment, qui portait la cle, disparait.
    window.history.replaceState(null, "", `${location.pathname}${location.search}`);
  }
  // Rechargement : plus de fragment ni de memoire, mais la copie de la page peut encore l'avoir.
  enMemoire ??= lireStockee();
  return enMemoire;
}

/** Pour les tests : oublie la cle gardee. */
export function oublierCle(): void {
  enMemoire = null;
}
